'use strict';

// Models sometimes invent this XML presentation instead of a supported card.
// Recover its data as an ordinary table; never execute or inject its markup.
const escapeText = value => value.replace(/&(?:amp|lt|gt|quot|apos|#\d{1,7}|#x[\da-f]{1,6});/gi, entity => {
    const named = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&apos;': "'" };
    if (named[entity]) return named[entity];
    if (!entity.startsWith('&#')) return entity;
    const code = parseInt(entity.slice(entity[2].toLowerCase() === 'x' ? 3 : 2, -1), entity[2].toLowerCase() === 'x' ? 16 : 10);
    return code > 0 && code <= 0x10ffff && !(code >= 0xd800 && code <= 0xdfff) ? String.fromCodePoint(code) : entity;
}).replace(/[\r\n\t ]+/g, ' ').trim()
    .replace(/\\/g, '\\\\').replace(/([`*_{}\[\]()#+|~])/g, '\\$1')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function readableComparison(raw, pending) {
    const body = raw.replace(/^<comparison-card>\s*/, '').replace(/<\/comparison-card>\s*$/, '');
    const title = /^\s*<title>([^<>]*)<\/title>/.exec(body);
    let rest = title ? body.slice(title[0].length) : body;
    const rows = [];
    while (rows.length < 100) {
        const row = /^\s*<row>\s*<label>([^<>]*)<\/label>\s*((?:<value>[^<>]*<\/value>\s*)+)<\/row>/.exec(rest);
        if (!row) break;
        const values = [...row[2].matchAll(/<value>([^<>]*)<\/value>/g)].map(match => escapeText(match[1]));
        if (values.length > 6) break;
        rows.push([escapeText(row[1]), ...values]);
        rest = rest.slice(row[0].length);
    }
    const heading = title ? `### ${escapeText(title[1])}\n\n` : '';
    if (rows.length && rows.every(row => row.length === rows[0].length) && (!rest.trim() || pending)) {
        const headers = ['Item', ...rows[0].slice(1).map((_, index, values) => values.length === 1 ? 'Value' : `Value ${index + 1}`)];
        return heading + [headers, headers.map(() => '---'), ...rows].map(row => `| ${row.join(' | ')} |`).join('\n');
    }
    if (pending) return heading + 'Preparing comparison…';
    // Malformed output must retain its numbers/text, even if it cannot form a
    // rectangular table. Escape unknown tags instead of interpreting HTML.
    return body.split(/<\/?(?:title|row|label|value)>/g).map(escapeText).filter(Boolean).join('\n\n');
}

function comparisonMarkupBlocks(content, streaming = false) {
    if (typeof content !== 'string' || !content.includes('<comparison-card>')) return [];
    if (/^\uFEFF?\s*(?:<!doctype\s+html\b|<html(?:\s|>))/i.test(content)) return [];
    const blocks = [];
    let fence = null, card = null, position = 0;
    for (const line of content.match(/[^\n]*(?:\n|$)/g) || []) {
        if (!line) continue;
        const marker = /^ {0,3}(`{3,}|~{3,})([^\n]*)/.exec(line);
        if (!card && marker) {
            if (!fence) fence = marker[1];
            else if (marker[1][0] === fence[0] && marker[1].length >= fence.length && !marker[2].trim()) fence = null;
        }
        if (!fence && !card && /^ {0,3}<comparison-card>(?:\s|<title>|$)/.test(line)) card = { start: position, rawStart: position + line.indexOf('<comparison-card>') };
        if (card) {
            const closing = line.indexOf('</comparison-card>');
            if (closing >= 0) {
                const end = position + closing + '</comparison-card>'.length;
                const raw = content.slice(card.rawStart, end);
                blocks.push({ start: card.start, end, raw, pending: false, fallback: readableComparison(raw, false) });
                card = null;
            }
        }
        position += line.length;
    }
    if (card) {
        const raw = content.slice(card.rawStart);
        blocks.push({ start: card.start, end: content.length, raw, pending: streaming, fallback: readableComparison(raw, streaming) });
    }
    return blocks;
}

function normalizeComparisonMarkup(content, streaming = false) {
    let result = content;
    for (const block of comparisonMarkupBlocks(content, streaming).reverse()) {
        result = result.slice(0, block.start) + '\n\n' + block.fallback + '\n\n' + result.slice(block.end);
    }
    return result;
}

module.exports = { comparisonMarkupBlocks, normalizeComparisonMarkup };
